/**
 * MegaMek's own words for the things a scenario changes.
 *
 * Copied from the pinned arena image: the rule descriptions from
 * `common/options/messages.properties`, and the Princess behaviour presets
 * from `mmconf/princessBehaviors.xml`. Nothing here is prose this project
 * wrote — the point is that a player reads the same sentence MegaMek would
 * show them.
 *
 * Regenerate rather than edit: both files are in the image, and a hand-typed
 * copy drifts from the game the match is actually played in.
 */

/** What a rule does, in MegaMek's words. Keyed by its option id. */
export const RULE_DESCRIPTIONS: Record<string, string> = {
  individual_initiative:
    "If checked, each unit has its own initiative roll, and must move in that fixed order. Good for zellbrigen or RPG style games. Incompatible with the lance movement options.",
  team_initiative:
    "When checked, teams roll initiative as one group. This team initiative is used to order the units according to the normal method. Player order on a team is determined by their own initiative.\\nChecked by default",
  double_blind:
    "If checked, enemy units will only be visible if they are in line of sight of one or more of your units, or within sensor range. \\nUnchecked by default.",
  tacops_start_fire:
    "If checked, fires may be set accidentally or intentionally.",
  no_clan_physical:
    "If checked, clan Meks may not make physical attacks. \\nUnchecked by default.",
  flamer_heat:
    "If checked, flamers deal both heat and damage. (BMM pg. 99) Otherwise, you have the option to choose heat or damage.",
  random_basements:
    "If this setting is turned on, all maps will use random basements",
};

/** One of MegaMek's shipped Princess behaviours. */
export type Preset = {
  name: string;
  knobs: Record<string, number>;
};

/**
 * The behaviours MegaMek ships, in its own order.
 *
 * A scenario that sets the same knobs as one of these is running that
 * behaviour, and naming it - "Striker" - says far more to a player than four
 * numbers do. The "(pPT)" suffix MegaMek puts on most of them is dropped: it
 * marks the pack they came from, not the behaviour.
 */
export const PRESETS: readonly Preset[] = [
  {
    name: "Skirmisher",
    knobs: {
      hyperAggression: 6,
      selfPreservation: 4,
      herdMentality: 4,
      bravery: 3,
      fallShame: 4,
    },
  },
  {
    name: "Sniper",
    knobs: {
      hyperAggression: 1,
      selfPreservation: 6,
      herdMentality: 2,
      bravery: 4,
      fallShame: 5,
    },
  },
  {
    name: "Scout",
    knobs: {
      hyperAggression: 8,
      selfPreservation: 6,
      herdMentality: 3,
      bravery: 7,
      fallShame: 4,
    },
  },
  {
    name: "LRM Missile Boat",
    knobs: {
      hyperAggression: 1,
      selfPreservation: 4,
      herdMentality: 3,
      bravery: 6,
      fallShame: 5,
    },
  },
  {
    name: "SRM Missile Boat",
    knobs: {
      hyperAggression: 6,
      selfPreservation: 6,
      herdMentality: 7,
      bravery: 6,
      fallShame: 5,
    },
  },
  {
    name: "Striker",
    knobs: {
      hyperAggression: 9,
      selfPreservation: 2,
      herdMentality: 7,
      bravery: 5,
      fallShame: 5,
    },
  },
  {
    name: "Juggernaut",
    knobs: {
      hyperAggression: 9,
      selfPreservation: 3,
      herdMentality: 5,
      bravery: 5,
      fallShame: 5,
    },
  },
  {
    name: "BERSERK v2",
    knobs: {
      hyperAggression: 10,
      selfPreservation: 3,
      herdMentality: 8,
      bravery: 2,
      fallShame: 5,
    },
  },
  {
    name: "Brawler",
    knobs: {
      hyperAggression: 4,
      selfPreservation: 5,
      herdMentality: 2,
      bravery: 8,
      fallShame: 5,
    },
  },
  {
    name: "ESCAPE",
    knobs: {
      hyperAggression: 3,
      selfPreservation: 10,
      herdMentality: 4,
      bravery: 8,
      fallShame: 5,
    },
  },
  {
    name: "DEFAULT",
    knobs: {
      hyperAggression: 5,
      selfPreservation: 5,
      herdMentality: 5,
      bravery: 5,
      fallShame: 5,
    },
  },
  {
    name: "DEFAULT v2 BRAVER",
    knobs: {
      hyperAggression: 5,
      selfPreservation: 3,
      herdMentality: 8,
      bravery: 2,
      fallShame: 5,
    },
  },
  {
    name: "COWARDLY",
    knobs: {
      hyperAggression: 1,
      selfPreservation: 10,
      herdMentality: 8,
      bravery: 2,
      fallShame: 8,
    },
  },
  {
    name: "Ambusher",
    knobs: {
      hyperAggression: 7,
      selfPreservation: 6,
      herdMentality: 4,
      bravery: 7,
      fallShame: 5,
    },
  },
];

/**
 * The name of the behaviour a set of knobs describes, or null when it is not
 * one MegaMek ships.
 *
 * Every knob the scenario set has to match. A scenario that sets three of the
 * five and leaves the rest at their defaults still matches, because the ones
 * it did not set are not part of what it asked for.
 */
export function presetNamed(knobs: Record<string, string>): string | null {
  const entries = Object.entries(knobs);
  if (entries.length === 0) return null;
  // Case-folded on both sides. These knobs are written in the scenario file
  // and arrive spelled the way MegaMek's own parser reads them - all
  // lowercase, `hyperaggression` - while the presets here carry MegaMek's
  // camel-cased field names. Comparing the two directly matched nothing but
  // `bravery`, which is the one knob spelled the same either way, so no
  // scenario could ever be named as a preset.
  for (const preset of PRESETS) {
    const folded = new Map(
      Object.entries(preset.knobs).map(([k, v]) => [
        k.toLowerCase(),
        String(v),
      ]),
    );
    if (entries.every(([k, v]) => folded.get(k.toLowerCase()) === v)) {
      return preset.name;
    }
  }
  return null;
}
